import { useCallback } from "react";

import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";
import { ChargebeePortalSection, useChargebee } from "providers/ChargebeeProvider";
import scraperApi from "api";

type BillingPortalSession = {
  provider?: "chargebee" | "stripe";
  url?: string | null;
  access_url?: string;
}

export const STRIPE_PORTAL_RETURN_STORAGE_KEY = "scraperapi.stripe.portal-return";

export function useChargebeePortals() {

  const { activeSubscription } = useActiveSubscription();
  const { chargebeeInstance, chargebeePortalSections } = useChargebee();


  const isStripeBilling = activeSubscription?.billingProvider === "stripe";
  const canUseStripePortal = activeSubscription?.capabilities?.canUseStripePortal === true;
  const isChargebeeReady = isStripeBilling
    ? canUseStripePortal
    : (!!chargebeeInstance && !!chargebeePortalSections);

  const openChargebeePortal = useCallback(
    async (sectionType: ChargebeePortalSection, options?: any) => {
      const openHostedBillingPortal = async () => {
        try {
          const session = await scraperApi.subscription.billingSessions() as BillingPortalSession;

          if (session?.provider === "stripe" && session.url) {
            try {
              window.sessionStorage.setItem(STRIPE_PORTAL_RETURN_STORAGE_KEY, "pending");
            } catch {
              // The redirect remains safe; the marker only triggers a post-return refresh.
            }
            window.location.assign(session.url);
            return true;
          }

          return false;
        } catch (_err) {
          return false;
        }
      };

      if (isStripeBilling) {
        if (!canUseStripePortal) return;
        await openHostedBillingPortal();
        return;
      }

      if (await openHostedBillingPortal()) return;
      if (!chargebeeInstance || !chargebeePortalSections) return;

      // init the portal session with token and secret from the backend
      chargebeeInstance.setPortalSession(scraperApi.subscription.billingSessions);

      chargebeeInstance
        .createChargebeePortal()
        .openSection(
          {
            params: { subscriptionId: activeSubscription?.chargebeeSubscriptionId },
            sectionType: chargebeePortalSections[sectionType]
          },
          { ...options }
        );
    },
    [ chargebeeInstance, chargebeePortalSections, activeSubscription, canUseStripePortal, isStripeBilling ]
  );

  const closeAllChargebeePortals = useCallback(() => {
    chargebeeInstance.closeAll();
  }, [ chargebeeInstance ]);

  const generatePayNowLink = useCallback(async () => {
    return (await scraperApi.subscription.payNowLink())?.url;
  }, []);


  return {
    isChargebeeReady,
    openChargebeePortal,
    closeAllChargebeePortals,
    generatePayNowLink
  };
}
